import React from 'react';
import {
  Box,
  Checkbox,
  TableCell,
  TableRow,
  Paper,
  useTheme,
  Table as MuiTable,
  TableContainer,
  TableHead,
  TableBody,
} from '@mui/material';
import { Typography, Icon, formatUTCDate } from '@fdha/web-ui-library';

import { DeliveryV2Admin } from './AffectedDeliveriesModal';

type DeliveryWithPartialFlag = DeliveryV2Admin & {
  isPartiallyWithin?: boolean;
};

interface DeliveryTableProps {
  deliveries: DeliveryWithPartialFlag[];
  selectedIds: string[];
  onToggleSelection: (deliveryId: string) => void;
  onSelectAll: (checked: boolean) => void;
  isCancelation?: boolean;
  getWeekOfCycle: (delivery: DeliveryV2Admin) => string;
}

const DeliveryTable: React.FC<DeliveryTableProps> = ({
  deliveries,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  isCancelation = false,
  getWeekOfCycle,
}) => {
  const theme = useTheme();
  const allSelected =
    deliveries.length > 0 && selectedIds.length === deliveries.length;
  const updateType = isCancelation ? 'canceled' : 'reactivated';

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        border: 'none',
        overflow: 'hidden',
      }}
    >
      <TableContainer>
        <MuiTable>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
              <TableCell padding="checkbox" sx={{ width: '48px' }}>
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < deliveries.length
                  }
                  title={allSelected ? 'Unselect All' : 'Select All'}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'medium' }}>
                Delivery to be {updateType}
              </TableCell>
              <TableCell sx={{ fontWeight: 'medium', width: '100px' }}>
                Week
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveries.map((delivery) => (
              <TableRow key={delivery.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(delivery.id)}
                    onChange={() => onToggleSelection(delivery.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography>
                    Delivery for{' '}
                    {formatUTCDate(delivery.deliveryDate, 'monthWeekDay')}
                  </Typography>
                  {isCancelation && delivery.isPartiallyWithin && (
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Icon
                        name="alert-triangle-outline"
                        fill={theme.palette.warning.main}
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 0.5,
                          color: theme.palette.warning.dark,
                        }}
                      >
                        This delivery is partially affected and might need
                        manual adjustments
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>{getWeekOfCycle(delivery)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Paper>
  );
};

export default DeliveryTable;
